import React from 'react';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import Typography from '@mui/material/Typography';
import NotaJogador from '../../components/NotaJogador';
import CompletouChip from '../../components/CompletouChip';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import PersonRemoveRoundedIcon from '@mui/icons-material/PersonRemoveRounded';
import { AlcaArrastar, estiloArraste } from './Team';
import SectionCard from '../../components/ui/SectionCard';
import EmptyState from '../../components/ui/EmptyState';
import PlayerAvatar from '../../components/ui/PlayerAvatar';
import TeamCrest from '../../components/ui/TeamCrest';
import tokens from '../../theme/tokens';
import TrofeusVitorias from '../../components/TrofeusVitorias';

const PROXIMOS = 4; // entram na próxima partida

// Legenda curta do jogador na fila: chegada, jogos e pote
const legenda = (player) =>
  [player.posicaoOriginal ? `Chegou ${player.posicaoOriginal}º` : null, `${player.jogos ?? 0} jogo${player.jogos === 1 ? '' : 's'}`, player.pote ? `Pote ${player.pote}` : null]
    .filter(Boolean)
    .join(' · ');

/**
 * Fila de espera (compacta, pensada para o celular). Com `arrastar`, o jogador pode ser arrastado para outra
 * posição da fila ou sobre um jogador de um time; subir, descer e remover ficam no menu ⋮.
 */
const WaitList = ({ waitList = [], onRemovePlayer, onUpdatePlayerPosition, title, arrastar, vitorias = {}, notas = {}, completacoes = {} }) => {
  const [openDialog, setOpenDialog] = React.useState(false);
  const [selectedPlayer, setSelectedPlayer] = React.useState(null);
  const [menu, setMenu] = React.useState(null); // { anchor, player, index }
  const temAcoes = Boolean(onUpdatePlayerPosition || onRemovePlayer);

  const handleConfirmRemove = () => {
    if (selectedPlayer) {
      onRemovePlayer(selectedPlayer.user); // Passa o user ID para a função de remoção
    }
    setOpenDialog(false);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedPlayer(null);
  };

  const acao = (fn) => {
    const alvo = menu;
    setMenu(null);
    fn(alvo);
  };

  return (
    <SectionCard title={title} subtitle={waitList.length > 0 ? `Os ${PROXIMOS} primeiros entram na próxima partida` : undefined}>
      {waitList.length === 0 ? (
        <EmptyState title="Fila vazia" />
      ) : (
        <Stack spacing={0.75}>
          {arrastar && (
            <Typography variant="caption" color="text.secondary">
              Arraste pela alça para mudar a posição ou solte sobre um jogador de um time para trocar.
            </Typography>
          )}
          {waitList.map((player, index) => {
            const proximo = index < PROXIMOS;
            return (
              <Stack
                key={player.user_name}
                direction="row"
                alignItems="center"
                spacing={1}
                data-drop={arrastar ? `espera|${player.user_name}` : undefined}
                sx={{
                  minHeight: 56,
                  pl: arrastar ? 0.25 : 1,
                  pr: 0.5,
                  py: 0.75,
                  borderRadius: 2,
                  border: 1,
                  borderColor: proximo ? tokens.color.success : 'divider',
                  bgcolor: proximo ? tokens.color.successSoft : 'background.paper',
                  ...(arrastar ? estiloArraste(arrastar.estado('espera', player)) : {}),
                }}
              >
                {arrastar && <AlcaArrastar onPointerDown={(e) => arrastar.iniciar(e, 'espera', player)} />}
                <Box
                  sx={{
                    width: 26,
                    height: 26,
                    flexShrink: 0,
                    borderRadius: '50%',
                    display: 'grid',
                    placeItems: 'center',
                    fontSize: '0.8rem',
                    fontWeight: 700,
                    bgcolor: proximo ? tokens.color.success : tokens.color.surfaceAlt,
                    color: proximo ? '#fff' : 'text.secondary',
                  }}
                >
                  {index + 1}
                </Box>
                <PlayerAvatar userId={player.guest ? null : player.user} name={player.user_name} size={36} />
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} noWrap sx={{ minWidth: 0 }}>{player.user_name}</Typography>
                    <TrofeusVitorias vitorias={vitorias[player.user_name] || 0} />
                    {player.team && <TeamCrest name={player.team} size={18} sx={{ flexShrink: 0 }} />}
                  </Stack>
                  <Typography variant="caption" color="text.secondary" noWrap component="div">{legenda(player)}</Typography>
                </Box>
                <CompletouChip vezes={completacoes[player.user_name]} />
                <NotaJogador info={notas[player.user_name]} />
                {temAcoes && (
                  <IconButton aria-label={`Ações de ${player.user_name}`} onClick={(e) => setMenu({ anchor: e.currentTarget, player, index })} sx={{ flexShrink: 0 }}>
                    <MoreVertRoundedIcon />
                  </IconButton>
                )}
              </Stack>
            );
          })}
        </Stack>
      )}

      <Menu anchorEl={menu?.anchor} open={Boolean(menu)} onClose={() => setMenu(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        {onUpdatePlayerPosition && (
          <MenuItem disabled={menu?.index === 0} onClick={() => acao((m) => onUpdatePlayerPosition('up', m.player.user_name))}>
            <ListItemIcon><ArrowUpwardIcon fontSize="small" /></ListItemIcon>
            Subir uma posição
          </MenuItem>
        )}
        {onUpdatePlayerPosition && (
          <MenuItem disabled={menu?.index === waitList.length - 1} onClick={() => acao((m) => onUpdatePlayerPosition('down', m.player.user_name))}>
            <ListItemIcon><ArrowDownwardIcon fontSize="small" /></ListItemIcon>
            Descer uma posição
          </MenuItem>
        )}
        {onRemovePlayer && (
          <MenuItem sx={{ color: 'error.main' }} onClick={() => acao((m) => { setSelectedPlayer(m.player); setOpenDialog(true); })}>
            <ListItemIcon><PersonRemoveRoundedIcon fontSize="small" color="error" /></ListItemIcon>
            Remover da pelada
          </MenuItem>
        )}
      </Menu>

      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>Remover jogador</DialogTitle>
        <DialogContent>
          <DialogContentText>Remover {selectedPlayer?.user_name} da pelada?</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDialog} color="inherit">Cancelar</Button>
          <Button onClick={handleConfirmRemove} color="error" variant="contained">Remover</Button>
        </DialogActions>
      </Dialog>
    </SectionCard>
  );
};

export default WaitList;
